// src/PO-Process/POReport.jsx

import { useState, useEffect, useRef } from "react";
import { Box, Grid, Button } from "@mui/material";
import dayjs from "dayjs";
import api from "../axiosConfig";
import Card, { CardContent } from "./components/ui/card";
import ContainedButton from "../components/ui/ContainedButton";
import { useNavigate} from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { ClientSideRowModelModule } from "ag-grid-community";
import { ModuleRegistry } from "ag-grid-community";
import { getUser } from "../helpers";

ModuleRegistry.registerModules([ClientSideRowModelModule]);

const POReport = ({ open }) => {
  const [selectedDate, setSelectedDate] = useState(
    dayjs().format("YYYY-MM-DD")
  );

  const user = getUser();

  const navigate = useNavigate();
  const handleBackClick = () => {
    navigate("/po-home");
  };

  const [report1, setReport1] = useState([]);
  const [colDefs1, setColDefs1] = useState([]);
  const gridRef = useRef(null);

  const [gridApi, setGridApi] = useState(null);

  const defaultDate = dayjs().add(2, "day").format("YYYY-MM-DD");

  const [startDate, setStartDate] = useState(defaultDate);
  const [endDate, setEndDate] = useState(defaultDate);

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const onColumnVisible = () => {
    if (gridApi) {
      gridApi.refreshClientSideRowModel("group");
      gridApi.refreshClientSideRowModel("aggregate");
    }
  };

  const updatePinnedRow = () => {
    if (!gridApi) return;

    gridApi.setGridOption("pinnedBottomRowData", [
      { DC: "Grand Total", PO_Value: calculateGrandTotal() }
    ]);

  };

  // -------------------------------------------------------------------
  // LOAD DATA
  // -------------------------------------------------------------------
  const loadReports = async () => {
    const res = await api.get("/po-report", {
      params: {
        start_date: startDate,
        end_date: endDate,
        user
      },
    });

    const r1 = (res.data.report1 || []).map(row => ({
      ...row,
      Quantity: row.Quantity ? Number(row.Quantity) : 0,

      // CLEAN PO VALUE - remove commas, keep only digits and period
      PO_Value: row.PO_Value
        ? Number(String(row.PO_Value).replace(/,/g, "")) 
        : 0
    }));

    setReport1(r1);

    if (r1.length > 0) {
      const orderedCols = [
        "DC",
        "Channel",
        "Pricelist",
        "Product_Code",
        "Product_Name",
        "Quantity",
        "PO_Value",
        "PO_Number",
        "Verified_Time",
        "Outlet_Code",
        "Beat",
        "Van",
        "Delivery_Date",
      ];

      const dynamicColDefs = orderedCols.map((key) => {
        if (key === "Quantity") {
          return {
            headerName: "Quantity",
            field: "Quantity",
            sortable: true,
            filter: true,
            resizable: true,
            enableValue: true,
            aggFunc: "sum",
            editable: false,
            enableRowGroup: false,
            enablePivot: false,
            cellStyle: params => {
              if (params.node.group || params.node.footer) {
                return { fontWeight: "bold" };
              }
            }
          };
        }

        if (key === "Delivery_Date") {
          return {
            headerName: "Delivery Date",
            field: "Delivery_Date",
            sortable: true,
            filter: true,
            resizable: true,
            enableRowGroup: true,
            valueFormatter: (params) => {
              if (!params.value) return "";
              // Ensure formatting as YYYY-MM-DD only
              return dayjs(params.value).format("YYYY-MM-DD");
            },
          };
        }

        if (key === "Verified_Time") {
          return {
            headerName: key,
            field: "Verified_Time",
            sortable: true,
            filter: true,
            resizable: true,
            enableRowGroup: true,
            valueFormatter: (params) => {
              if (!params.value) return "";
              // DO NOT convert to Date(). Just return cleaned string
              return params.value.replace("GMT", "").trim();
            }
          };
        }

        // All other columns except Quantity are groupable
        return {
          headerName: key,
          field: key,
          sortable: true,
          filter: true,
          resizable: true,
          enableRowGroup: key !== "Quantity",
          enablePivot: key !== "Quantity",
        };
      });

      setColDefs1(dynamicColDefs);
    }
  };

  useEffect(() => {
    if (gridApi) {
      gridApi.refreshClientSideRowModel('aggregate');
    }
  }, [colDefs1, report1, gridApi]);

  // -------------------------------------------------------------------
  // FULL AG GRID SETTINGS FOR PIVOT TABLE (same as current-month-summary)
  // -------------------------------------------------------------------
  const sideBar = {
    toolPanels: [
      {
        id: "columns",
        labelDefault: "Columns",
        iconKey: "columns",
        toolPanel: "agColumnsToolPanel",
      },
    ],
    defaultToolPanel: null,
  };

  const autoGroupColumnDef = {
    minWidth: 200,
    suppressCount: false,
  };

  useEffect(() => {
    loadReports();   // run once on page load
  }, []);

  const calculateGrandTotal = () => {
    if (!gridApi) return 0;

    // Only visible rows after filters / grouping
    const visibleNodes = [];
    gridApi.forEachNodeAfterFilterAndSort((node) => {
      if (!node.group) visibleNodes.push(node);
    });

    // Unique PO Numbers
    const uniquePOs = new Map();
    for (const node of visibleNodes) {
      const row = node.data;
      if (!uniquePOs.has(row.PO_Number)) {
        uniquePOs.set(row.PO_Number, Number(row.PO_Value) || 0);
      }
    }

    return Array.from(uniquePOs.values()).reduce((sum, v) => sum + v, 0);
  };

  return (
    <div
      className="ag-table-container"
      style={{ marginLeft: open ? "240px" : "0px" }}
    >
      <Box sx={{ mt: 2 }}>
        <Card>
          <CardContent>

            {/* Filters Row */}
            <Box display="flex" gap={2} mb={3}>
              <div style={{ display: "flex", gap: "16px" }}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <label style={{ fontSize: "11px", fontWeight: 600, marginBottom: "4px" }}>
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column" }}>
                  <label style={{ fontSize: "11px", fontWeight: 600, marginBottom: "4px" }}>
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>

              <ContainedButton onClick={loadReports}>
                Load Report
              </ContainedButton>

              <ContainedButton onClick={handleBackClick}>
                Go to Homepage
              </ContainedButton>

            </Box>

            {/* REPORT TABLE */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box
                  className="ag-theme-quartz"
                  style={{
                    height: "calc(100vh - 220px)",
                    minHeight: "300px",
                    width: "100%"
                  }}
                >
                <AgGridReact
                  ref={gridRef}
                  rowData={report1}
                  columnDefs={colDefs1}
                  pivotMode={false}             // disable pivot for now
                  groupDisplayType="singleColumn"
                  autoGroupColumnDef={{
                    headerName: "Group",
                    minWidth: 200,
                    suppressCount: true,
                    cellRenderer: (params) => {
                      const label = params.node.key ?? params.value ?? "";
                      return (
                        <span
                          dangerouslySetInnerHTML={{
                            __html: `<span style="font-weight:bold;">${label}</span>`
                          }}
                        />
                      );
                    }
                  }}
                  sideBar={sideBar}
                  animateRows={true}
                  groupDefaultExpanded={-1}
                  suppressAggFuncInHeader={true}
                  onGridReady={onGridReady}
                  pinnedBottomRowData={[
                    { DC: "Grand Total", PO_Value: calculateGrandTotal() }
                  ]}
                  onFilterChanged={() => updatePinnedRow()}
                  onSortChanged={() => updatePinnedRow()}
                  onColumnRowGroupChanged={() => updatePinnedRow()}
                  onColumnPivotChanged={() => updatePinnedRow()}
                  onColumnVisible={updatePinnedRow}
                  
                />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </div>
  );
};

export default POReport;
